import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TOTAL_DURATION = 2500;
const TOTAL_DURATION_MOBILE = 800;

const IntroLoader = ({ onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Skip intro for: returning visitors, mobile (perf), or when running
  // under the react-snap puppeteer crawler (so the prerendered HTML
  // captures real page content, not the loader overlay).
  const isReactSnap =
    typeof navigator !== 'undefined' &&
    /ReactSnap/i.test(navigator.userAgent || '');
  const skipIntro =
    isReactSnap ||
    (typeof window !== 'undefined' &&
      (!!sessionStorage.getItem('gripItIntroSeen') || window.innerWidth < 768));
  const [visible, setVisible] = useState(!skipIntro);

  const dismiss = useCallback(() => {
    sessionStorage.setItem('gripItIntroSeen', 'true');
    setVisible(false);
    onCompleteRef.current?.();
  }, []);

  useEffect(() => {
    if (skipIntro) {
      onCompleteRef.current?.();
      return;
    }

    const duration = window.innerWidth < 768 ? TOTAL_DURATION_MOBILE : TOTAL_DURATION;
    setTimeout(dismiss, duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;

  const letterText = 'GRIP IT';
  const drawDur = 0.8;
  const fillStart = 0.8;
  const textStart = 1.2;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-900"
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.4 }}
          onClick={dismiss}
          role="status"
          aria-live="polite"
          aria-label="Loading Grip It"
        >
          <div className="flex flex-col items-center gap-8">
            <svg viewBox="0 0 200 280" className="w-32 h-44 sm:w-40 sm:h-56" fill="none">
              <defs>
                <linearGradient id="brandGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#64B2FF" />
                  <stop offset="50%" stopColor="#5396BD" />
                  <stop offset="100%" stopColor="#3F6BAA" />
                </linearGradient>
              </defs>

              <motion.path d="M30 40 L170 40" stroke="#64B2FF" strokeWidth="6" strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut' }} />
              <motion.path d="M70 40 L70 60 Q70 75 80 80 L80 90" stroke="#64B2FF" strokeWidth="4" strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut', delay: 0.05 }} />
              <motion.path d="M130 40 L130 60 Q130 75 120 80 L120 90" stroke="#64B2FF" strokeWidth="4" strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut', delay: 0.05 }} />
              <motion.circle cx="100" cy="70" r="14" stroke="#64B2FF" strokeWidth="3"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut' }} />
              <motion.circle cx="100" cy="70" r="14" fill="url(#brandGrad)"
                initial={{ opacity: 0 }} animate={{ opacity: 0.8 }}
                transition={{ duration: 0.4, delay: fillStart }} />
              <motion.path d="M100 84 L100 160" stroke="#64B2FF" strokeWidth="5" strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut', delay: 0.1 }} />
              <motion.path d="M100 160 L80 220 L75 260" stroke="#64B2FF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut', delay: 0.15 }} />
              <motion.path d="M100 160 L120 220 L125 260" stroke="#64B2FF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: drawDur, ease: 'easeInOut', delay: 0.2 }} />
            </svg>

            <div className="flex gap-1.5 text-3xl sm:text-4xl font-heading font-bold text-white tracking-widest">
              {letterText.split('').map((char, i) => (
                <motion.span key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: textStart + i * 0.06 }}
                  style={{ display: 'inline-block' }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-48 h-1 bg-brand-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, #5396BD, #64B2FF, #3F6BAA)' }}
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: TOTAL_DURATION / 1000, ease: 'easeInOut' }}
            />
          </div>

          <motion.button
            className="absolute bottom-16 left-1/2 -translate-x-1/2 text-xs text-white/30 hover:text-white/60 transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            onClick={dismiss}
          >
            Click anywhere to skip
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroLoader;
